import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FracturedHalo } from './FracturedHalo';
export const WhyDeploid: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 1.4, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0, 0.5, 1, 0.5, 0]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const titleScale = useTransform(scrollYProgress, [0, 0.3, 0.5], [0.8, 1.1, 1]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const reasons = [{
    title: 'Proven Industry Experience',
    description: '30+ years of combined experience across SaaS, cloud and operational transformation—supporting organisations from startups to global enterprises.'
  }, {
    title: 'Technical Excellence',
    description: 'We combine deep technical understanding with practical delivery discipline to build AI systems that integrate smoothly, perform consistently and scale confidently.'
  }, {
    title: 'Deployment Focus',
    description: 'We prioritise real outcomes. Every solution we deliver is designed to move beyond concepts into production—driving measurable impact from day one.'
  }, {
    title: 'Responsible AI Framework',
    description: 'Transparent governance, ethical safeguards and clear oversight ensure every deployment is safe, trusted and aligned with responsible AI principles.'
  }];
  return <section id="why-deploid" ref={sectionRef} className="relative py-24 md:py-32 bg-black overflow-hidden" data-id="element-151">
      <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{
      scale,
      opacity,
      rotate
    }} data-id="element-152">
        <FracturedHalo size={900} segments={16} opacity={0.4} data-id="element-153" />
      </motion.div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-154">
        <motion.div className="text-center max-w-3xl mx-auto mb-20" style={{
        scale: titleScale,
        opacity: titleOpacity
      }} data-id="element-155">
          <motion.h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-light tracking-wider mb-6 text-neon-blue glow-text" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-156">
            Why DEPLOID
          </motion.h2>
          <motion.p className="text-xl text-neon-blue/80" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-157">
            We don't just build AI. We deploy the future.
          </motion.p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16" data-id="element-158">
          {reasons.map((reason, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: index % 2 === 0 ? -50 : 50,
          rotateY: index % 2 === 0 ? -20 : 20
        }} whileInView={{
          opacity: 1,
          x: 0,
          rotateY: 0
        }} transition={{
          duration: 0.8,
          delay: index * 0.15,
          ease: 'easeOut'
        }} viewport={{
          once: true,
          amount: 0.3
        }} className="relative" data-id="element-159">
              <motion.div className="absolute -left-3 top-0 w-[1px] h-full bg-gradient-to-b from-neon-blue/0 via-neon-blue/30 to-neon-blue/0" initial={{
            scaleY: 0
          }} whileInView={{
            scaleY: 1
          }} transition={{
            duration: 0.8,
            delay: index * 0.15 + 0.3
          }} viewport={{
            once: true
          }} data-id="element-160" />
              <motion.h3 className="text-2xl md:text-3xl font-heading font-light mb-4 text-neon-blue" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: index * 0.15 + 0.2
          }} viewport={{
            once: true
          }} data-id="element-161">
                {reason.title}
              </motion.h3>
              <motion.p className="text-neon-blue/70" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: index * 0.15 + 0.4
          }} viewport={{
            once: true
          }} data-id="element-162">
                {reason.description}
              </motion.p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};